import { useWindowDimensions, Platform } from "react-native";
import {
  BoardObjectProps,
  CellProps,
  ClassicGameStatistics,
  GameVariant,
} from "../../../../Functions/LocalDatabase";
import { calculateGameScore } from "./DifficultyFunctions";
import { finishGame, saveGame } from "../../../../Api/Puzzles";
import { isEqual } from "../../Drill/Functions/CellFunctions";
import {
  ACTION_ROW_HEIGHT_IN_CELLS_DESKTOP,
  ACTION_ROW_HEIGHT_IN_CELLS_MOBILE,
  HEADER_ROW_HEIGHT_IN_CELLS_DESKTOP,
  HEADER_ROW_HEIGHT_IN_CELLS_MOBILE,
  MOBILE_BREAKPOINT,
  NAV_HEADER_HEIGHT,
  NAV_HEADER_SAFETY_PADDING,
  NUMBER_CONTROL_HEIGHT_IN_CELLS,
  PUZZLE_HEIGHT_IN_CELLS,
  isNavHeaderVisible,
} from "../../../../Functions/GameLayout";
export { formatTime } from "../../../../Functions/Utils";
/**
 * This is a temporary place to store functions
 * todo functions will be documented, sorted, and optimized
 */

const MAX_BOARD_SIZE = 640;
const BOARD_EDGE_PADDING = 1;

const BOARD_VERTICAL_VIEWPORT_FRACTION_DESKTOP = 0.92;
const BOARD_VERTICAL_VIEWPORT_FRACTION_MOBILE = 0.98;

/**
 * This function retrieves the user's device size and calculates the cell size
 * board has width and height dimensions
 */
export function useCellSize(): number {
  const { width, height } = useWindowDimensions();

  // Web uses a single monotonic sizing rule (no breakpoint branches), so
  // board size never increases while viewport width is shrinking.
  if (Platform.OS === "web") {
    const webBoardLayoutHeightInCells =
      HEADER_ROW_HEIGHT_IN_CELLS_MOBILE +
      PUZZLE_HEIGHT_IN_CELLS +
      ACTION_ROW_HEIGHT_IN_CELLS_MOBILE +
      NUMBER_CONTROL_HEIGHT_IN_CELLS;
    const maxCellSizeFromWidth =
      Math.min(Math.max(width - BOARD_EDGE_PADDING, 0), MAX_BOARD_SIZE) / 9;

    const navHeaderShown = isNavHeaderVisible(width, height);
    const availableHeightWithHeader =
      height - NAV_HEADER_HEIGHT - NAV_HEADER_SAFETY_PADDING;

    const maxCellSizeFromHeight =
      (navHeaderShown ? availableHeightWithHeader : height) /
      webBoardLayoutHeightInCells;
    return Math.min(maxCellSizeFromWidth, maxCellSizeFromHeight);
  }

  const isMobileSizingLayout = width < MOBILE_BREAKPOINT;
  const headerRowHeightInCells = isMobileSizingLayout
    ? HEADER_ROW_HEIGHT_IN_CELLS_MOBILE
    : HEADER_ROW_HEIGHT_IN_CELLS_DESKTOP;
  const actionRowHeightInCells = isMobileSizingLayout
    ? ACTION_ROW_HEIGHT_IN_CELLS_MOBILE
    : ACTION_ROW_HEIGHT_IN_CELLS_DESKTOP;

  const boardLayoutHeightInCells =
    headerRowHeightInCells +
    PUZZLE_HEIGHT_IN_CELLS +
    actionRowHeightInCells +
    NUMBER_CONTROL_HEIGHT_IN_CELLS;

  const boardVerticalViewportFraction = isMobileSizingLayout
    ? BOARD_VERTICAL_VIEWPORT_FRACTION_MOBILE
    : BOARD_VERTICAL_VIEWPORT_FRACTION_DESKTOP;

  const maxBoardWidth = Math.max(
    (isMobileSizingLayout
      ? Math.min(width, MAX_BOARD_SIZE)
      : Math.min(width * 0.9, MAX_BOARD_SIZE)) - BOARD_EDGE_PADDING,
    0,
  );

  const maxCellSizeFromWidth = maxBoardWidth / 9;

  const navHeaderShown = isNavHeaderVisible(width, height);
  const availableHeightWithHeader =
    height - NAV_HEADER_HEIGHT - NAV_HEADER_SAFETY_PADDING;

  const maxCellSizeFromHeight =
    ((navHeaderShown ? availableHeightWithHeader : height) *
      boardVerticalViewportFraction) /
    boardLayoutHeightInCells;

  return Math.min(maxCellSizeFromWidth, maxCellSizeFromHeight);
}

export const isValueCorrect = (
  solution: CellProps | number,
  inputValue: number,
): boolean => {
  if (typeof solution === "object" && "entry" in solution) {
    return isEqual(solution.entry, inputValue);
  }
  return solution === inputValue;
};

/**
 * Calculates and returns the score of the game.
 * Calls Puzzle.finishGame to update Statistics Page and remove localstorage object.
 * @param difficulty
 * @param numHintsUsed
 * @param numWrongCellsPlayed
 * @param time
 * @returns
 */
export function finishSudokuGame(
  statistics: ClassicGameStatistics,
  variant: GameVariant,
): ClassicGameStatistics {
  // calculate score
  const score = calculateGameScore(
    statistics.numHintsUsed,
    statistics.numWrongCellsPlayed,
    statistics.time,
    statistics.difficulty,
  );

  // removes game from localstorage and updates statistics page
  finishGame(
    statistics.numHintsUsed,
    statistics.numHintsUsedPerStrategy,
    statistics.numWrongCellsPlayed,
    statistics.time,
    score,
    variant,
  );

  statistics.score = score;

  return {
    numHintsUsed: statistics.numHintsUsed,
    numHintsUsedPerStrategy: statistics.numHintsUsedPerStrategy,
    numWrongCellsPlayed: statistics.numWrongCellsPlayed,
    time: statistics.time,
    score: statistics.score,
    difficulty: statistics.difficulty,
    internalDifficulty: statistics.internalDifficulty,
  };
}

/**
 * Saves the game and navigates back a screen
 * @param sudokuBoard The current state of the game
 * @param navigation The navigation object
 */
export function handlePause(sudokuBoard: BoardObjectProps, navigation: any) {
  saveGame(sudokuBoard);
  navigation.navigate("PlayPage");
}
